import React, { useId, useState } from 'react';
import PropTypes from 'prop-types';
import Decimal from 'decimal.js';

import { getPopoverTriggerAria } from '~/components/ui/atoms/Popover/usePopoverState';
import { isReinsuranceManagementClaim } from '~/Utils/ClaimUtils';

import { useClaim } from '../ClaimContainer';
import { useCms } from '../hooks/useCms';
import { usePaymentsConfiguration } from '../hooks/usePaymentsConfiguration';
import HoverActionField from '../HoverActionField';

import SumContainer from './ExposuresTable/SumContainer';
import ExposureTooltipedLock from './ExposureTooltipedLock';
import {
  getPayableWithReserveType,
  isExposureWriteDisabled,
  isPayableWithReservePending,
  PENDING_COLOR,
} from './ExposureUtils';
import ReserveChangeRequestContainer from './ReserveChangeRequestContainer';

function ReserveContainer(props) {
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const popoverId = useId();

  const { claim, onClaimUpdate } = useClaim();
  const { user, userOrganization } = useCms();
  const { isExpensePaymentAllowedWithCoverageIssue, reduceReservesByNetPaid } = usePaymentsConfiguration();

  const { exposure, payableWithReserve, title } = props;
  const {
    reserve,
    are_pending_reserve_change_requests_exist,
    reserve_change_request_pending,
    are_pending_payment_requests_exist,
    pending_sum,
    net_pending_sum,
    net_pending_amounts,
  } = payableWithReserve;

  const payableType = getPayableWithReserveType(exposure, payableWithReserve);

  function handleClosePopover() {
    setPopoverAnchorEl(null);
  }

  function getReserveToShow() {
    if (are_pending_reserve_change_requests_exist) return reserve_change_request_pending.request_amount;

    if (are_pending_payment_requests_exist && !isReinsuranceManagementClaim(claim, userOrganization)) {
      // Approval reduces the reserve by the net amount when reserves are reduced by net paid, so the
      // preview follows it -- showing gross under netting previews a reserve the approval never produces.
      // `net_pending_amounts.sum` is the amount approval will actually take out: converted into policy
      // currency and clamped per request, unlike the aggregate `net_pending_sum`, which subtracts each
      // request's `vat_amount` in the request's own currency and clamps in aggregate. That one is the
      // fallback only for a payload from before the field existed.
      const pendingReserveReduction = reduceReservesByNetPaid
        ? (net_pending_amounts?.sum ?? net_pending_sum)
        : pending_sum;
      // Money arithmetic in `Decimal`, like the netting terms this figure has to agree with, rather than
      // on doubles. No observable change on its own -- the only consumer is `SumContainer`, which
      // renders through `currencyFormatter.format`, and `Decimal.max(0, ...)` is exactly the
      // `reserveAfterApproval < 0` clamp it replaces. `?? 0` guards the constructor: `new
      // Decimal(undefined)` throws where the subtraction merely went `NaN`.
      return Decimal.max(0, new Decimal(reserve ?? 0).minus(pendingReserveReduction ?? 0)).toNumber();
    }

    return reserve;
  }

  const disableChangeReserve =
    are_pending_payment_requests_exist ||
    isExposureWriteDisabled(exposure, user, { enableIfDenied: payableType === 'expenses' });

  if (exposure.is_coverage_issue_exists) {
    const isExemptFromCoverageIssueLock = payableType === 'expenses' && isExpensePaymentAllowedWithCoverageIssue;
    if (!isExemptFromCoverageIssueLock) {
      return <ExposureTooltipedLock exposure={exposure} />;
    }
  }

  const cardProps = { isPopover: true, open: Boolean(popoverAnchorEl), anchorEl: popoverAnchorEl, id: popoverId };

  return (
    <>
      <HoverActionField
        onAction={(e) => setPopoverAnchorEl(e.currentTarget)}
        disabled={disableChangeReserve}
        permanent
        actionAriaLabel={`Set ${payableType} reserve`}
        actionTriggerAria={getPopoverTriggerAria(Boolean(popoverAnchorEl), popoverId)}
      >
        <SumContainer
          sum={getReserveToShow()}
          color={isPayableWithReservePending(payableWithReserve) ? PENDING_COLOR : undefined}
          fontWeight={exposure?.isGeneralExpenses ? 700 : undefined}
        />
      </HoverActionField>
      <ReserveChangeRequestContainer
        claim={claim}
        onUpdate={onClaimUpdate}
        payableWithReserve={payableWithReserve}
        exposure={exposure}
        reserveChangeRequest={reserve_change_request_pending}
        cardDialogProps={title ? { ...cardProps, title } : cardProps}
        onClose={handleClosePopover}
        refreshInBackground
      />
    </>
  );
}

ReserveContainer.propTypes = {
  exposure: PropTypes.object.isRequired,
  payableWithReserve: PropTypes.object.isRequired,
  title: PropTypes.string,
};

export default ReserveContainer;
